export type { Environment, Theme, DrawerDirection } from "./types";
export { resolveEnvironment } from "./lib/environment";
export {
  useChainDisplay,
  useChainsConfig,
  getChainDisplay,
  CHAINS_CONFIG_FILE,
} from "./lib/remote-config";
export type { ChainDisplayMeta, ChainsConfig } from "./lib/remote-config";
export { announceAppLauncherUsage } from "./lib/runtime-log";
export type { AppLauncherUsageMode } from "./lib/runtime-log";
export {
  initDappUiSentry,
  getDappUiSentryScope,
  captureDappUiException,
  DappUiErrorBoundary,
  trackDappUiEvent,
  trackDappUiFunnel,
  setDappUiAnalyticsUser,
  normalizeFailureReason,
} from "./lib/sentry/index";
export type {
  InitDappUiSentryOptions,
  DappUiErrorBoundaryProps,
  DappUiFeature,
  DappUiFlow,
  DappUiFailureReason,
  TrackDappUiFunnelOptions,
} from "./lib/sentry/index";

import { announceAppLauncherUsage } from "./lib/runtime-log";

announceAppLauncherUsage({ mode: "dapp-ui" });

export {
  AppLauncher,
  AppLauncherTrigger,
  AppLauncherContent,
  useGlobalMenu,
} from "./features/app-launcher/index";
export type {
  AppLauncherProps,
  AppLauncherTriggerProps,
  AppLauncherTriggerStyle,
  AppLauncherContentProps,
  GlobalMenu,
  GlobalMenuItemAssetUrl,
  GlobalMenuItem,
  GlobalMenuItemUrl,
  GlobalMenuItemServiceStatus,
} from "./features/app-launcher/index";

export {
  WalletInfo,
  ConnectorId,
  CONNECTOR_REGISTRY,
  useTokenBalance,
  USER_BALANCE_QUERY_KEY,
  useTokenStats,
  TOKEN_STATS_QUERY_KEY,
} from "./features/wallet-info/index";
export type {
  ChainId,
  PreferredToken,
  ConnectorMeta,
  WalletInfoProps,
  WalletInfoStyle,
  WalletInfoTriggerProps,
  WalletInfoContentProps,
  WalletInfoNavProps,
  WalletInfoFooterProps,
  TokenBalance,
  TokenBalanceResponse,
  TokenStats,
  TokenStatsResponse,
} from "./features/wallet-info/index";

export {
  WalletPortfolio,
  WalletPortfolioBody,
  PORTFOLIO_SECTIONS,
  WALLET_PORTFOLIO_QUERY_PREFIX,
  invalidateWalletPortfolioQueries,
} from "./features/wallet-portfolio/index";
export type {
  PortfolioSection,
  WalletPortfolioProps,
  WalletPortfolioTriggerProps,
  WalletPortfolioContentProps,
  WalletPortfolioBodyProps,
  EstimateGasArgs,
  EstimateGasFn,
  GasEstimate,
  GetTransactionReceiptArgs,
  GetTransactionReceiptFn,
  SendTransactionArgs,
  SendTransactionFn,
  TransactionReceiptResult,
  LpBalanceInfo,
  LpBalanceReaderFn,
  StakingRewardsInfo,
  StakingRewardsReaderFn,
  GameSwapPool,
  GameSwapTokenRef,
  CrossdPosition,
  CrossdPositionPoolRef,
  CrossdPositionTokenRef,
  CrossdPositionDetail,
} from "./features/wallet-portfolio/index";

export { SendFlow } from "./features/send/index";
export type {
  SendFlowProps,
  RecentSendAddress,
  SendAccount,
  SendPageProps,
  SendAsset,
  SendStatus,
} from "./features/send/index";

export {
  OnePopBody,
  OnePopGetTokenTrigger,
} from "./features/onepop/index";
export type {
  OnePopBodyProps,
  OnePopActivityDirection,
  OnePopActivityItem,
  OnePopActivityStatus,
  OnePopSummary,
} from "./features/onepop/index";

export { BridgeFlow } from "./features/bridge/index";
export type {
  BridgeAmountSource,
  BridgeApprovalInfo,
  BridgeApproveFn,
  BridgeFailedInfo,
  BridgeGetApprovalFn,
  BridgeFlowProps,
  BridgeGetToTokensFn,
  BridgeHistoryItem,
  BridgeInfoRow,
  BridgeInfoTokenRef,
  BridgeLiquidityInfo,
  BridgePathType,
  BridgeQuoteFn,
  BridgeQuoteInput,
  BridgeQuoteResult,
  BridgeStatus,
  BridgeStep,
  BridgeSubmitFn,
  BridgeSubmittedInfo,
  BridgeToken,
  BridgeTxSummary,
} from "./features/bridge/index";

export {
  GetOneUsd,
  findGetOneUsdToken,
  getOneUsdTokenKey,
  isGetOneUsdActionEnabled,
  isGetOneUsdActionSupported,
  isGetOneUsdActionVisible,
  isGetOneUsdTargetAvailable,
  matchesGetOneUsdToken,
  resolveGetOneUsdRoute,
} from "./features/get-one-usd/index";
export type {
  GetOneUsdActionId,
  GetOneUsdBridgeProps,
  GetOneUsdButtonComponent,
  GetOneUsdEvent,
  GetOneUsdMode,
  GetOneUsdRemoteModes,
  GetOneUsdPairs,
  GetOneUsdProps,
  GetOneUsdRelayProps,
  GetOneUsdTokenRef,
  GetOneUsdWaitForTransaction,
  ResolvedGetOneUsdRoute,
} from "./features/get-one-usd/index";

export type {
  OnOutlink,
  OutlinkContext,
  OutlinkCategory,
  OutlinkOrigin,
} from "./lib/outlink";

export {
  WalletConnectModal,
  WALLET_REGISTRY,
  SOCIAL_REGISTRY,
  useWalletDetect,
} from "./features/wallet-connect-modal/index";
export type {
  WalletConnectModalProps,
  WalletConnectModalTriggerProps,
  WalletConnectModalContentProps,
  WalletConnectModalStyle,
  WalletHandlers,
  WalletConfig,
  WalletId,
  SocialHandlers,
  SocialConfig,
  SocialId,
} from "./features/wallet-connect-modal/index";

export {
  ConnectButton,
  APPLE_ICON,
  BINANCE_ICON,
  CROSSX_ICON,
  GOOGLE_ICON,
  METAMASK_ICON,
} from "./features/connect-button/index";
export type {
  ConnectButtonProps,
  ConnectButtonStyle,
  WalletProvider,
} from "./features/connect-button/index";

export { SkillsButton, DEFAULT_SKILLS_HREF } from "./features/skills/index";
export type { SkillsButtonProps, SkillsButtonStyle } from "./features/skills/index";

export {
  RelayDeposit,
  RelayHistory,
  RelayRecovery,
  useRelayConfig,
  useRelayOrders,
  useRelayRecovery,
  // Presentational single-order stepper -- not used by the deposit wizard, but
  // exported for hosts polling GET /v1/orders/{id} themselves. No name clash at
  // this barrel, so it needs no relay-specific alias.
  StatusTracker,
  createRelayClient,
  RelayApiError,
  DEFAULT_TRUSTED_FACTORIES,
} from "./features/relay/index";
export type {
  RelayClient,
  RelayClientOptions,
  StatusTrackerProps,
  RelayDepositProps,
  RelayHistoryProps,
  RelayRecoveryProps,
  RelayDepositTriggerProps,
  RelayDepositContentProps,
  RelayHistoryTriggerProps,
  RelayHistoryContentProps,
  RelayRecoveryTriggerProps,
  RelayRecoveryContentProps,
  UseRelayConfigOptions,
  UseRelayConfigResult,
  UseRelayOrdersOptions,
  UseRelayOrdersResult,
  UseRelayRecoveryOptions,
  UseRelayRecoveryResult,
  RelayRecoveryQueryKey,
  RelayRecoveryQueryOptions,
  RelayRecoveryAction,
  RelayRecoveryState,
  RelayRecoveryStep,
  RelayWalletProps,
  RecoverySuccessResult,
  RelayTheme,
  RelayDrawerDirection,
  // `SendTransactionFn` already exported from wallet-portfolio above --
  // re-exported under a relay-specific alias to avoid a duplicate-identifier
  // clash at this barrel.
  SendTransactionFn as RelaySendTransactionFn,
  WriteContractFn,
  ReadContractFn,
  SwitchChainFn,
  WaitForReceiptFn,
  GetBalanceFn,
  RelayBalance,
  RelayTxRequest,
  RelayContractCall,
  // Bare `Hex` collides with viem/ethers' identically-named type in consumer
  // code -- re-exported under a relay-specific alias here, same precedent as
  // `RelaySendTransactionFn` above. The feature barrel itself still exports
  // the unaliased `Hex` (no clash inside that namespace).
  Hex as RelayHex,
  Catalog,
  OriginOption,
  DepositAddressResult,
  QuoteResult,
  OrderSummary,
  RecoveryInfo,
} from "./features/relay/index";
